import { yupResolver } from '@hookform/resolvers/yup';
import { Box, makeStyles } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import AppleButton from '~/pages/auth/shared/components/AppleButton';
import AuthPaper from '~/pages/auth/shared/components/AuthPaper';
import GoogleButton from '~/pages/auth/shared/components/GoogleButton';
import OrDivider from '~/pages/auth/shared/components/OrDivider';
import useAppleAuthEnabled from '~/pages/auth/shared/hooks/useAppleAuthEnabled';
import useGoogleAuthEnabled from '~/pages/auth/shared/hooks/useGoogleAuthEnabled';
import Copyright from '~/shared/components/Copyright';
import Link from '~/shared/components/Link';
import PasswordInput from '~/shared/components/PasswordInput';
import TextField from '~/shared/components/TextField';
import {
  FORGOT_PASSWORD_ROUTE,
  SIGN_UP_ROUTE,
} from '~/shared/constants/routes';
import {
  IdentityProvider,
  ProviderCredential,
} from '~/shared/services/userService';

const schema = yup.object().shape({
  email: yup.string().email().required().label('Email address'),
  password: yup.string().required().label('Password'),
});

export type FormShape = {
  email: string;
  password: string;
};

type Props = {
  onSubmit: (values: FormShape) => Promise<void>;
  onProviderCredential: (
    provider: IdentityProvider,
    credential: ProviderCredential
  ) => Promise<void>;
  onProviderError: (provider: IdentityProvider) => void;
};

const SignIn: FC<Props> = ({
  onSubmit,
  onProviderCredential,
  onProviderError,
}) => {
  const classes = useStyles();

  const googleAuthEnabled = useGoogleAuthEnabled();
  const appleAuthEnabled = useAppleAuthEnabled();
  const socialAuthEnabled = googleAuthEnabled || appleAuthEnabled;

  const [isLoading, setIsLoading] = useState(false);

  const { errors, handleSubmit, register } = useForm<FormShape>({
    resolver: yupResolver(schema),
  });

  const handleSignIn = async (values: FormShape) => {
    setIsLoading(true);

    try {
      await onSubmit(values);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleProviderCredential =
    (provider: IdentityProvider) => async (credential: ProviderCredential) => {
      setIsLoading(true);

      try {
        await onProviderCredential(provider, credential);
      } finally {
        setIsLoading(false);
      }
    };

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      component="main"
      className={classes.root}
    >
      <AuthPaper title="Sign in">
        <form onSubmit={handleSubmit(handleSignIn)} noValidate>
          <Grid container direction="column" spacing={3}>
            <Grid item>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                inputRef={register}
                error={Boolean(errors['email'])}
                helperText={errors['email']?.message}
              />

              <PasswordInput
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                autoComplete="current-password"
                inputRef={register}
                error={Boolean(errors['password'])}
                helperText={errors['password']?.message}
              />
            </Grid>

            <Grid item>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={isLoading}
              >
                {'Sign in'}
              </Button>
            </Grid>

            {socialAuthEnabled && (
              <Grid item>
                <OrDivider />
                {googleAuthEnabled && (
                  <Box mt={2}>
                    <GoogleButton
                      text="signin_with"
                      onCredential={handleProviderCredential('google')}
                      onError={() => onProviderError('google')}
                      disabled={isLoading}
                    />
                  </Box>
                )}
                {appleAuthEnabled && (
                  <Box mt={2}>
                    <AppleButton
                      text="signin_with"
                      onCredential={handleProviderCredential('apple')}
                      onError={() => onProviderError('apple')}
                      disabled={isLoading}
                    />
                  </Box>
                )}
              </Grid>
            )}

            <Grid item>
              <Grid container direction="column" spacing={1}>
                <Grid item>
                  <Link
                    href={FORGOT_PASSWORD_ROUTE}
                    variant="body2"
                    color="inherit"
                  >
                    {'Forgot password?'}
                  </Link>
                </Grid>

                <Grid item>
                  <Link href={SIGN_UP_ROUTE} variant="body2" color="inherit">
                    {"Don't have an account? Sign Up"}
                  </Link>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Copyright mt={5} />
        </form>
      </AuthPaper>
    </Grid>
  );
};

const useStyles = makeStyles(() => ({
  root: {
    height: '100vh',
  },
}));

export default SignIn;
